import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Driver, Severity, mockAlerts } from '../data/mockData';
import { AlertTriangle, Info, WifiOff } from 'lucide-react';
interface FleetMapProps {
  drivers: Driver[];
  selectedDriverId: string | null;
  highlightedDriverIds?: string[] | null;
  onSelectDriver: (id: string) => void;
  onDeselect?: () => void;
  demoState: string;
}
export function FleetMap({
  drivers,
  selectedDriverId,
  highlightedDriverIds,
  onSelectDriver,
  onDeselect,
  demoState
}: FleetMapProps) {
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  // Filter out sleeper/inactive drivers from the map
  const visibleDrivers = drivers.filter(d => d.activityStatus !== 'sleeper');
  // Map dimensions
  const width = 800;
  const height = 600;
  // Compute status counts
  const getStatusCategory = (status: Severity): string => {
    if (status === 'critical') return 'critical';
    if (status === 'warning') return 'warning';
    if (status === 'offline') return 'offline';
    return 'ontrack';
  };
  const statusCounts = {
    critical: visibleDrivers.filter(d => d.status === 'critical').length,
    warning: visibleDrivers.filter(d => d.status === 'warning').length,
    ontrack: visibleDrivers.filter(d => d.status !== 'critical' && d.status !== 'warning' && d.status !== 'offline').length,
    offline: visibleDrivers.filter(d => d.status === 'offline').length
  };
  const getColor = (status: Severity) => {
    switch (status) {
      case 'critical':
        return '#ef4444';
      // brand-red
      case 'warning':
        return '#f59e0b';
      // brand-amber
      case 'info':
        return '#10b981';
      // treat info same as normal/green
      case 'offline':
        return '#6b7280';
      // slate-500
      default:
        return '#10b981';
      // brand-green
    }
  };
  return <div className="w-full h-full relative bg-[#0a0f1a] overflow-hidden flex items-center justify-center" onClick={() => {
    onDeselect?.();
    setStatusFilter(null);
  }} data-id="element-284">
      {/* Background Grid / City Map Illusion */}
      <div className="absolute inset-0 opacity-20 pointer-events-none" data-id="element-285">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-286">
          <defs data-id="element-287">
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse" data-id="element-288">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#1f2937" strokeWidth="1" data-id="element-289" />
            </pattern>
            <pattern id="grid-large" width="160" height="160" patternUnits="userSpaceOnUse" data-id="element-290">
              <rect width="160" height="160" fill="url(#grid)" data-id="element-291" />
              <path d="M 160 0 L 0 0 0 160" fill="none" stroke="#374151" strokeWidth="1.5" data-id="element-292" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid-large)" data-id="element-293" />

          {/* Fake highways */}
          <path d="M 0 200 Q 400 300 800 100" fill="none" stroke="#1f2937" strokeWidth="8" data-id="element-294" />
          <path d="M 200 0 Q 300 400 100 800" fill="none" stroke="#1f2937" strokeWidth="6" data-id="element-295" />
          <path d="M 0 500 Q 500 500 800 600" fill="none" stroke="#1f2937" strokeWidth="6" data-id="element-296" />
        </svg>
      </div>

      {/* Map Content Container */}
      <div className="relative w-full h-full max-w-[800px] max-h-[600px]" data-id="element-297">
        {/* Render Bridge Hazard if in bridge state */}
        {demoState === 'bridge' && <motion.div initial={{
        scale: 0,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} className="absolute z-20 flex flex-col items-center" style={{
        left: '48%',
        top: '48%',
        transform: 'translate(-50%, -50%)'
      }} data-id="element-298">
            <div className="w-12 h-12 bg-brand-amber/20 rounded-full flex items-center justify-center animate-pulse" data-id="element-299">
              <AlertTriangle className="w-6 h-6 text-brand-amber" data-id="element-300" />
            </div>
            <div className="bg-slate-900 border border-brand-amber/50 text-brand-amber text-[10px] font-bold px-2 py-1 rounded mt-1 shadow-lg" data-id="element-301">
              LOW BRIDGE 12'8"
            </div>
          </motion.div>}

        {/* Render Route Line for selected driver */}
        {selectedDriverId && <svg className="absolute inset-0 w-full h-full pointer-events-none z-10" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-302">
            {visibleDrivers.map(d => {
          if (d.id !== selectedDriverId) return null;
          const x = d.location.x;
          const y = d.location.y;
          const isBridgeState = demoState === 'bridge' && d.id === 'd-david';
          return <g key={`route-${d.id}`} data-id="element-303">
                  {/* Actual/Planned Route */}
                  <path d={`M ${x} ${y} Q ${x + 7} ${y - 7} ${x + 15} ${y - 10}`} fill="none" stroke={isBridgeState ? '#ef4444' : getColor(d.status)} strokeWidth="0.4" strokeDasharray={isBridgeState ? 'none' : '0.8 0.5'} className="opacity-60" vectorEffect="non-scaling-stroke" data-id="element-304" />

                  {/* Suggested Alternate Route for Bridge State */}
                  {isBridgeState && <path d={`M ${x} ${y} Q ${x + 10} ${y + 5} ${x + 15} ${y - 10}`} fill="none" stroke="#10b981" strokeWidth="0.4" strokeDasharray="0.5 0.5" className="opacity-80 animate-[dash_1s_linear_infinite]" vectorEffect="non-scaling-stroke" data-id="element-305" />}
                </g>;
        })}
          </svg>}

        {/* Render Trucks */}
        {visibleDrivers.map(driver => {
        const isSelected = driver.id === selectedDriverId;
        const isHighlighted = highlightedDriverIds?.includes(driver.id) ?? false;
        const shouldDim = highlightedDriverIds && highlightedDriverIds.length > 0 && !isHighlighted;
        const isFilteredOut = statusFilter !== null && getStatusCategory(driver.status) !== statusFilter;
        const color = getColor(driver.status);
        return <motion.div key={driver.id} className="absolute z-30 cursor-pointer group hover:!z-50" style={{
          left: `${driver.location.x}%`,
          top: `${driver.location.y}%`
        }} onClick={e => {
          e.stopPropagation();
          onSelectDriver(driver.id);
        }} whileHover={{
          scale: isFilteredOut ? 1 : 1.2
        }} animate={{
          scale: isSelected ? 1.3 : isHighlighted ? 1.15 : 1,
          zIndex: isSelected ? 50 : isHighlighted ? 45 : 30,
          opacity: isFilteredOut ? 0.08 : shouldDim ? 0.2 : selectedDriverId && !isSelected ? 0.3 : 1
        }} data-id="element-306">
              {/* Pulse effect for critical/selected/highlighted */}
              {(driver.status === 'critical' || isSelected || isHighlighted) && !isFilteredOut && <span className="absolute inset-0 rounded-full animate-ping opacity-75" style={{
            backgroundColor: color,
            transform: 'scale(2.5)'
          }} data-id="element-307" />}

              {/* Marker */}
              <div className={`w-4 h-4 rounded-full border-2 shadow-lg relative z-10 transition-colors
                  ${isSelected || isHighlighted ? 'border-white' : 'border-slate-900'}`} style={{
            backgroundColor: color
          }} data-id="element-308" />

              {/* Tooltip / Label */}
              {(() => {
            const severityOrder: Record<Severity, number> = {
              critical: 0,
              warning: 1,
              offline: 2,
              info: 3,
              normal: 4
            };
            const driverAlerts = mockAlerts.filter(a => a.driverId === driver.id || a.affectedDriverIds?.includes(driver.id)).sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);
            const topAlert = driverAlerts[0] || null;
            const alertColorMap: Record<string, string> = {
              critical: 'text-brand-red',
              warning: 'text-brand-amber',
              info: 'text-brand-blue',
              offline: 'text-slate-400'
            };
            return <div className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 shadow-xl whitespace-nowrap pointer-events-none transition-opacity max-w-[200px]
                    ${isSelected || isHighlighted ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`} data-id="element-309">
                    <div className="text-xs font-bold text-slate-200" data-id="element-310">
                      {driver.truckId}
                    </div>
                    <div className="text-[10px] text-slate-400" data-id="element-311">
                      {driver.name}
                    </div>
                    {topAlert && <div className={`flex items-center gap-1 mt-1 pt-1 border-t border-slate-800 ${driver.status === 'normal' || driver.status === 'info' ? 'text-slate-500' : alertColorMap[topAlert.severity] || 'text-slate-400'}`} data-id="element-312">
                        {topAlert.severity === 'critical' || topAlert.severity === 'warning' ? <AlertTriangle className="w-3 h-3 shrink-0" data-id="element-313" /> : topAlert.severity === 'offline' ? <WifiOff className="w-3 h-3 shrink-0" data-id="element-314" /> : <Info className="w-3 h-3 shrink-0" data-id="element-315" />}
                        <span className="text-[10px] font-semibold truncate" data-id="element-316">
                          {topAlert.title}
                        </span>
                      </div>}
                  </div>;
          })()}
            </motion.div>;
      })}
      </div>

      {/* Status Filter Bar */}
      <div className="absolute top-4 left-[88px] flex items-center gap-1.5 z-20" onClick={e => e.stopPropagation()} data-id="element-317">
        <StatusFilterPill color="bg-brand-red" label="Critical" count={statusCounts.critical} isActive={statusFilter === 'critical'} onClick={() => setStatusFilter(statusFilter === 'critical' ? null : 'critical')} data-id="element-318" />
        <StatusFilterPill color="bg-brand-amber" label="Warning" count={statusCounts.warning} isActive={statusFilter === 'warning'} onClick={() => setStatusFilter(statusFilter === 'warning' ? null : 'warning')} data-id="element-319" />
        <StatusFilterPill color="bg-brand-green" label="On Track" count={statusCounts.ontrack} isActive={statusFilter === 'ontrack'} onClick={() => setStatusFilter(statusFilter === 'ontrack' ? null : 'ontrack')} data-id="element-320" />
      </div>
    </div>;
}
function StatusFilterPill({
  color,
  label,
  count,
  isActive,
  onClick
}: {
  color: string;
  label: string;
  count: number;
  isActive: boolean;
  onClick: () => void;
}) {
  return <button onClick={onClick} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all border
        ${isActive ? 'bg-slate-800 border-slate-600 text-slate-100 shadow-sm' : 'bg-slate-900/80 border-slate-800 text-slate-400 hover:text-slate-200 hover:border-slate-700'}`} data-id="element-321">
      <div className={`w-2 h-2 rounded-full ${color}`} data-id="element-322" />
      {label}
      <span className={`text-[10px] font-bold ${isActive ? 'text-slate-300' : 'text-slate-500'}`} data-id="element-323">
        {count}
      </span>
    </button>;
}